import cc from 'classcat';
import { useId, useRef, useMemo, useCallback, useEffect } from 'react';
import { TranscriptButton } from '@pm2/element-toolbar';
import { resets } from '@ui/css';
import { xml2ast, allHtmlParsers, printHtmlNodesToJsx } from '@utils/ast';
import { UniversalContextMenu } from '@utils/component-framework';
import { PostloadedImage } from '@utils/react';
import { useIsPrintContent } from '../../print-context.js';
import { imageWrapperCls, contextMenu, imageCls, creditingCls, containerCls } from './Image.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

const Image = ({
  sheetRef,
  pageId,
  pageAspectRatio,
  zIndex,
  item,
  isSelected,
  isCloned,
  onSelect,
  onDragStart,
  Before,
  After
}) => {
  const id = useId();
  const ref = useRef(null);
  const crediting = formatCrediting(item);
  const isPrintContent = useIsPrintContent();
  const imageLongDescAST = useMemo(() => xml2ast(item.longDesc ?? "", allHtmlParsers, "text/html"), [item.longDesc]);
  const handleSelect = useCallback(evt => {
    if (onSelect !== undefined) {
      onSelect(evt, item);
    }
  }, [item, onSelect]);
  useEffect(() => {
    const node = ref.current;
    if (node === null) {
      return;
    }
    node.addEventListener("focusin", handleSelect, {
      passive: false
    });
    node.addEventListener("mousedown", handleSelect, {
      passive: false
    });
    node.addEventListener("touchstart", handleSelect, {
      passive: false
    });
    return () => {
      node.removeEventListener("focusin", handleSelect);
      node.removeEventListener("mousedown", handleSelect);
      node.removeEventListener("touchstart", handleSelect);
    };
  }, [item, handleSelect]);
  const handlePointerDragStart = evt => {
    if (!item.settings.cloneable && !item.settings.movable) {
      return;
    }
    onDragStart?.(evt, item);
  };
  const handleKeyDown = evt => {
    if ((evt.key === " " || evt.key === "Enter") && !document.body.classList.contains("isDragging")) {
      onDragStart?.(evt, item);
    }
  };
  return (
    /*#__PURE__*/
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    jsxs("div", {
      ref: ref,
      onMouseDown: handlePointerDragStart,
      onTouchStart: handlePointerDragStart,
      onKeyDown: handleKeyDown,
      "data-drag-target": true,
      "data-id": item.id,
      className: cc([containerCls]),
      style: {
        zIndex,
        left: `${(item.position.x * 100).toString()}%`,
        top: `${(item.position.y * 100).toString()}%`,
        width: `${(item.size.width * 100).toString()}%`,
        height: `${(item.size.height * 100).toString()}%`,
        transform: item.angleInRadian !== 0 ? `rotate(${item.angleInRadian.toString()}rad)` : undefined
      },
      children: [Before !== undefined && /*#__PURE__*/jsx(Before, {
        sheetRef: sheetRef,
        pageId: pageId,
        pageAspectRatio: pageAspectRatio,
        isSelected: isSelected,
        isCloned: isCloned,
        item: item
      }), /*#__PURE__*/jsxs("div", {
        className: imageWrapperCls,
        children: [!!imageLongDescAST.length && !isPrintContent && /*#__PURE__*/jsx("div", {
          className: contextMenu,
          children: /*#__PURE__*/jsx(UniversalContextMenu, {
            layout: "solid",
            label: "\xC5bn kontekstmenuen",
            children: /*#__PURE__*/jsx(TranscriptButton, {
              nodeType: "image-component",
              transcript: imageLongDescAST
            }, "transcript")
          })
        }), /*#__PURE__*/jsx(PostloadedImage, {
          alt: item.alt ?? "",
          className: imageCls,
          src: item.src,
          showImmediately: isPrintContent,
          loading: isPrintContent ? "eager" : undefined,
          "aria-details": !!imageLongDescAST.length && !isPrintContent ? `transcript-${id}` : undefined
        }), !!imageLongDescAST.length && !isPrintContent && /*#__PURE__*/jsx("div", {
          className: resets.screenReaderOnlyCls,
          id: `transcript-${id}`,
          "data-disable-polly": true,
          children: printHtmlNodesToJsx(imageLongDescAST)
        })]
      }), crediting !== undefined && /*#__PURE__*/jsx("span", {
        className: creditingCls,
        children: crediting
      }), After !== undefined && /*#__PURE__*/jsx(After, {
        sheetRef: sheetRef,
        pageId: pageId,
        pageAspectRatio: pageAspectRatio,
        isSelected: isSelected,
        isCloned: isCloned,
        item: item,
        onDragStart: onDragStart
      })]
    })
  );
};
function formatCrediting(image) {
  if (!image.settings.showCrediting) {
    return undefined;
  }
  let crediting = "";
  crediting = appendCreditingSegment(crediting, image.crediting.title);
  crediting = appendCreditingSegment(crediting, image.crediting.description);
  crediting = appendCreditingSegment(crediting, image.crediting.author);
  crediting = appendCreditingSegment(crediting, image.crediting.creditLine);
  crediting = appendCreditingSegment(crediting, image.crediting.year);
  return crediting.length > 0 ? crediting : undefined;
}
function appendCreditingSegment(crediting, segment) {
  const trimmedSegment = segment.trim();
  if (trimmedSegment.length === 0) {
    return crediting;
  }
  return crediting.length > 0 ? `${crediting}, ${trimmedSegment}` : trimmedSegment;
}

export { Image };
//# sourceMappingURL=Image.js.map
